import React from 'react';
import PropTypes from 'prop-types';
import MorePropTypes from 'airbnb-prop-types';
import { withStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import Close from '@material-ui/icons/Close';
import Fab from '@material-ui/core/Fab';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { uiState } from 'react-redux-ui-state';
import classnames from 'classnames';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ACL_KINDS from 'constants/aclKinds';
import { required, validateCidr, aclItemUnique, maxLength } from 'services/validationMsgs';
import { normalizeCidr } from 'services/normalizer';
import RBACSubmitButton from 'components/RBACWrapper/RBACSubmitButton/RBACSubmitButton';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import RocketChip from '../MissionCtrl/RocketChip/RocketChip';
import ORTypography from '../ORTypography/ORTypography';

/**
 * Appropriate throw an error with the relevant validation errors if necessary
 *
 * @param {result} value - Result of the form submission
 * @throws {error} - Throws SubmissionError if any errors were returned
 */
const handleFormSubmissionReturn = (result) => {
  if (result.error) {
    throw new SubmissionError(result.formErrors);
  }
};

const propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    addButton: PropTypes.string.isRequired,
    aclButton: PropTypes.string.isRequired,
    formStatus: PropTypes.string.isRequired,
    kindContainer: PropTypes.string.isRequired,
    fieldContainer: PropTypes.string.isRequired,
    buttonContainer: PropTypes.string.isRequired,
    chipContainer: PropTypes.string.isRequired,
  }).isRequired,
  newInstanceAcls: PropTypes.instanceOf(Array), // eslint-disable-line react/no-unused-prop-types
  selectedKind: PropTypes.number.isRequired,
  defaultSelectedKind: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  instanceService: PropTypes.string.isRequired,
  instanceId: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  addInstanceAcl: PropTypes.func.isRequired,
  getIp: PropTypes.func.isRequired,
  getIpClear: PropTypes.func.isRequired,
  myip: PropTypes.string,
  error: PropTypes.string,
  change: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  closePopout: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.func]),
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  formType: PropTypes.string,
  reset: PropTypes.func.isRequired,
};

const defaultProps = {
  newInstanceAcls: [],
  error: null,
  closePopout: null,
  myip: null,
  formType: '',
};

const maxLength128 = maxLength(128);

/**
 * Wrapper function for the redux-form aclUniqueItem validation
 *
 * @param {string} value - value of field with validation
 * @param {string} formValues - values of all fields in the form
 * @param {object} props - component props
 * @returns {undefined || string} - Returns undefined if no error, message
 * if there is an error
 */
export function aclItemUniqueValidation(value, formValues, props) {
  const payload = {
    cidr: formValues.cidr,
    name: formValues.name,
    kind: props.selectedKind,
  };
  return aclItemUnique(null, null, { newAcl: payload, newInstanceAcls: props.newInstanceAcls });
}

/**
 * Checks to see if there are any kinds compatible with the selected instance service
 *
 * @param {result} value - Result of the form submission
 * @returns {boolean} - Returns true if there is at least one compatible kind
 */
const serviceCompatibleAcl = (instanceService) => {
  let match = false;
  Object.values(ACL_KINDS).forEach((value) => {
    if (value.service === instanceService) {
      match = true;
    }
  });
  return match;
};

export function UnstyledAclForm({
  classes,
  selectedKind,
  defaultSelectedKind,
  setUiState,
  instanceService,
  instanceId,
  handleSubmit,
  submitSucceeded,
  submitting,
  addInstanceAcl,
  error,
  change,
  touch,
  pristine,
  valid,
  getIp,
  getIpClear,
  myip,
  closePopout,
  userRoles,
  formType,
  reset,
}) {
  /**
   * Fills 0.0.0.0/0 into cidr form field
   *
   */
  const handleAnyIpClick = () => {
    const ipWildcard = '0.0.0.0/0';
    change('cidr', ipWildcard);
    touch('cidr');
    change('name', 'Allow All');
    touch('name');
  };

  /**
   * Selects an ACL kind (role) by setting it to a prop.
   *
   * @param {string} kind - Accepts the kind you would like to select
   */
  const handleKindClick = (event, name, kind) => {
    change(name, kind);
    setUiState({
      selectedKind: kind,
    });
  };

  /**
   * Submission handler called when the form is executed. Checks
   * for ACL uniqueness.
   *
   * @param {object} data - data posted from the aclPopoutItem form
   * @throws {error} - Throws SubmissionError if ACL is unique.
   */
  const submitHandler = (data) =>
    addInstanceAcl(instanceId, instanceService, data).then(handleFormSubmissionReturn);

  if (submitSucceeded && closePopout) {
    closePopout();
  }

  const errorElm = error ? (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  ) : null;

  const aclTitle = serviceCompatibleAcl(instanceService) ? (
    <ORTypography component="p" className={classes.roleTextBlurb}>
      Set a Role for the IP:
    </ORTypography>
  ) : null;

  if (!defaultSelectedKind) {
    const aclKinds = Object.values(ACL_KINDS);
    const defaultChip = aclKinds.find((el) => el.service === instanceService && el.label);

    if (defaultChip) {
      setUiState({
        selectedKind: defaultChip.kind,
        defaultSelectedKind: true,
      });
      change('kind', defaultChip.kind);
    }
  }

  const aclKindChips = Object.keys(ACL_KINDS)
    .sort()
    .map((key) => {
      const el = ACL_KINDS[key];
      if (el.service === instanceService && ACL_KINDS[key].label) {
        return (
          <RocketChip
            key={el.kind}
            label={el.label}
            onClick={(event) => handleKindClick(event, 'kind', el.kind)}
            isSelected={el.kind === selectedKind}
            clickable
          />
        );
      }
      return null;
    });

  if (myip) {
    change('cidr', myip);
    touch('cidr');
    getIpClear();
    change('name', 'My IP');
    touch('name');
  }

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <div className={classes.buttonContainer}>
        <Button
          hover-override="green"
          className={classes.aclButton}
          onClick={handleAnyIpClick}
          value="0.0.0.0/0"
          variant="contained"
        >
          AllOW ANY IP
        </Button>
        <ORTypography align="center" variant="subtitle1" className={classes.ORText}>
          -OR-
        </ORTypography>
        <Button
          name="useMyIP"
          hover-override="green"
          className={classes.myIPButton}
          onClick={(event) => getIp(event)}
          variant="contained"
        >
          USE MY IP
        </Button>
      </div>
      <div className={classes.fieldContainer}>
        <Field
          name="cidr"
          label="Cidr"
          variant="outlined"
          placeholder="#.#.#.#/#"
          component={WrappedTextField}
          className={classes.aclInput}
          validate={[aclItemUniqueValidation, validateCidr, required]}
          normalize={normalizeCidr}
          props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
          fullWidth
        />
        <Field
          name="name"
          label="Description"
          variant="outlined"
          placeholder="Home Office"
          component={WrappedTextField}
          className={classnames(classes.aclInput, classes.descInput)}
          props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
          validate={[maxLength128, required]}
          fullWidth
        />
      </div>
      <div className={classes.kindContainer}>
        {aclTitle}
        <div className={classes.chipContainer}>{aclKindChips}</div>
        {errorElm}
        <div
          className={
            formType !== 'embedded'
              ? classes.addButtonContainer
              : classes.embeddedAddButtonContainer
          }
        >
          {formType === 'embedded' && (
            <Fab
              size="small"
              color="secondary"
              className={classes.cancelButton}
              onClick={() => reset()}
            >
              <Close fontSize="large" />
            </Fab>
          )}
          <RBACSubmitButton
            userAccess={userRoles}
            allowedAccess={[OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER]}
            submitting={submitting}
            disabled={!valid || pristine}
          />
        </div>
      </div>
    </form>
  );
}

UnstyledAclForm.propTypes = propTypes;
UnstyledAclForm.defaultProps = defaultProps;

const styles = (theme) => ({
  root: {
    zIndex: '1300',
    maxWidth: '100%',
    display: 'flex',
  },
  addButton: {
    margin: theme.spacing(2),
  },
  addButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  embeddedAddButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelButton: {
    margin: theme.spacing(2),
  },
  addButtonLabel: {
    flexGrow: 3,
  },
  aclButton: {
    color: 'white',
    backgroundColor: theme.customColors.green,
    margin: '1rem 0 1rem 1rem',
    flex: '1 1 45%',
  },
  aclInput: {
    [theme.breakpoints.down('sm')]: {
      '& input': {
        fontSize: '4.5vw',
      },
    },
  },
  descInput: {
    marginTop: theme.spacing(2),
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  formStatus: {
    margin: `${theme.spacing(2)}px auto auto auto`,
    fontSize: '.8rem',
    fontStyle: 'italic, Roboto Regular',
    maxWidth: '300px',
  },
  kindContainer: {
    textAlign: 'left',
  },
  fieldContainer: {
    margin: theme.spacing(1),
    '& input': {
      textAlign: 'center',
    },
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'unset',
      maxWidth: 'unset',
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    flexBasis: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 ${theme.spacing(2)}px`,

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },

    '& [role|=button]': {
      marginTop: 0,
    },
  },
  chipColorPrimary: {
    backgroundColor: theme.customColors.peach,
    color: theme.customColors.black,
  },
  ORText: {
    flex: '1 1 10%',
    fontWeight: 'bold',
    margin: `0 ${theme.spacing(0.5)}px`,
  },
  myIPButton: {
    color: 'white',
    backgroundColor: theme.customColors.green,
    margin: '1rem 1rem 1rem 0',
    flex: '1 1 45%',
  },
  roleTextBlurb: {
    margin: `${theme.spacing(1)}px 0`,
    fontSize: '1rem',
    marginLeft: theme.spacing(2),
  },
});

export const uiConfig = {
  name: 'instanceAclFormItem',
  state: () => ({
    selectedKind: 1,
    defaultSelectedKind: false,
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(reduxForm({ form: 'newAcl' })(UnstyledAclForm)),
);
